import {useConfig} from "@revenuewell/logic-config";
import {useOidcWithAdmin} from "@revenuewell/logic-oidc";
import {usePatientProfile} from "../use-patient-profile";
import {useNotification} from "@revenuewell/logic-notification";
import {useEffect, useMemo, useState} from "react";
import {PatientConnect365Enrollment} from "../../services/api-client/contact-api.types";
import {ContactApi} from "../../services/api-client/contact-api";
const GREEN_COLOR = 'rgba(0, 138, 6, 1)';
const GRAY_COLOR = 'rgba(173, 173, 173, 1)';

export function usePatientConnect365() {
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { contactDetails, locationId } = usePatientProfile();
  const { showError, showSuccess } = useNotification();

  const [pc365Enrollment, setPc365Enrollment] = useState<PatientConnect365Enrollment>({
    alreadyEnrolled: false,
    isReadyToSendEnrollment: false,
    validEmailAddress: false,
    isEnrollAvailable: false,
    inactiveUser: false,
  });
  const [isPending, setIsPending] = useState(false);

  const [showEnrollmentLink, setShowEnrollmentLink] = useState(false);
  const [showEnrollmentStatus, setShowEnrollmentStatus] = useState(false);
  const [statusBackgroundColor, setStatusBackgroundColor] = useState('');
  const [statusLabel, setStatusLabel] = useState('');

  const contactApi = useMemo(() => ContactApi.initialize(config, oidcService), []);

  const processPatientConnect365Enrollment = (pc365Enrollment: PatientConnect365Enrollment) => {
    let showEnrollmentStatus = false;
    let showEnrollmentLink = false;
    let backgroundColor = '';
    let label = '';

    if (pc365Enrollment.alreadyEnrolled) {
      showEnrollmentStatus = true;
      backgroundColor = GREEN_COLOR;
      label = 'Enrolled';
    } else if (pc365Enrollment.isReadyToSendEnrollment && pc365Enrollment.validEmailAddress && pc365Enrollment.isEnrollAvailable && !pc365Enrollment.inactiveUser) {
      showEnrollmentStatus = true;
      showEnrollmentLink = true;
      backgroundColor = GRAY_COLOR;
      label = 'Not enrolled';
    } else if (!pc365Enrollment.validEmailAddress && pc365Enrollment.isEnrollAvailable) {
      showEnrollmentStatus = true;
      backgroundColor = GRAY_COLOR;
      label = 'Not enrolled';
    }

    return {showEnrollmentStatus, showEnrollmentLink, backgroundColor, label};
  };

  useEffect(() => {
    const loadPatientConnect365Enrollment = async () => {
      try {
        const pc365Enrollment = await contactApi.getPatientConnect365Enrollment(contactDetails.id, locationId);
        const {showEnrollmentStatus, showEnrollmentLink, backgroundColor, label} = processPatientConnect365Enrollment(pc365Enrollment);

        setPc365Enrollment(pc365Enrollment);
        setShowEnrollmentStatus(showEnrollmentStatus);
        setShowEnrollmentLink(showEnrollmentLink);
        setStatusBackgroundColor(backgroundColor);
        setStatusLabel(label);
      } catch {
        showError('Unable to load Patient Connect 365 enrollment data.');
      }
    };

    loadPatientConnect365Enrollment();
  }, [])

  const handleInviteClick = async () => {
    if (pc365Enrollment.isReadyToSendEnrollment) {
      try {
        await contactApi.sendPatientConnect365Invitation(contactDetails.id, locationId);
        showSuccess('The invitation was sent.');
        setIsPending(true);
      } catch {
        showError('Unable to send invitation.');
      }
    }
  };

  return {pc365Enrollment, handleInviteClick, isPending, showEnrollmentLink, showEnrollmentStatus, statusBackgroundColor, statusLabel};
}
